import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const CARDS = [
  { id: 'web',        wide: false },
  { id: 'arch',       wide: false },
  { id: 'cloud',      wide: true  },
  { id: 'mobile',     wide: false },
  { id: 'security',   wide: false },
  { id: 'automation', wide: true  },
  { id: 'network',    wide: false },
  { id: 'policy',     wide: false },
  { id: 'marketing',  wide: false },
  { id: 'av',         wide: false },
  { id: 'it',         wide: true  },
];

const CARD_TITLES = {
  web: 'Web Development', arch: 'Smart Architecture', cloud: 'Cloud & DevOps',
  mobile: 'Mobile First', security: 'Fortress Security', automation: 'Automation & Integrations',
  network: 'Networking & Infrastructure', policy: 'Digital Policy & Compliance',
  marketing: 'Digital Marketing', av: 'AV System Consulting',
  it: 'IT Support & Maintenance',
};

const ScanLine = () => (
  <motion.div
    className="absolute left-0 right-0 h-px pointer-events-none z-10"
    style={{ background: 'linear-gradient(90deg, transparent, #f97316, transparent)' }}
    initial={{ top: '-2px', opacity: 0 }}
    animate={{ top: ['0%', '100%'], opacity: [0, 0.7, 0.7, 0] }}
    transition={{ duration: 2.2, repeat: Infinity, repeatDelay: 4, ease: 'linear' }}
  />
);

const WebDevCard = ({ isActive: _isActive }) => (
  <div className="flex flex-col justify-between h-full">
    <div className="flex items-start justify-between">
      <span className="text-xs font-mono text-gray-600 tracking-widest uppercase">01</span>
      <span className="text-xs font-black text-orange-500 tracking-widest">+65% SPEED</span>
    </div>
    <div>
      <div className="text-[2.6rem] font-black leading-none text-white tracking-tight">WEB</div>
      <div className="text-[2.6rem] font-black leading-none tracking-tight"
        style={{ color: 'transparent', WebkitTextStroke: '1.5px rgba(255,255,255,0.2)' }}>DEV</div>
    </div>
    <div className="font-mono text-xs text-gray-600 tracking-wider">REACT · NEXT.JS · PWA</div>
  </div>
);

const ArchCard = ({ isActive: _isActive }) => (
  <div className="flex flex-col justify-between h-full">
    <span className="text-xs font-mono text-gray-600 tracking-widest uppercase">02</span>
    <div>
      <div className="text-sm font-light text-gray-500 tracking-[0.25em] uppercase mb-1">Smart</div>
      <div className="text-2xl font-black text-white uppercase leading-tight tracking-tight">Architecture</div>
      <div className="text-2xl font-black text-orange-500 uppercase leading-tight tracking-tight">+80%</div>
    </div>
    <div className="text-[10px] text-gray-700 tracking-widest uppercase">Scalability</div>
  </div>
);

const CloudCard = ({ isActive: _isActive }) => (
  <div className="flex items-center justify-between h-full gap-6">
    <div className="shrink-0">
      <div className="font-mono font-black text-white leading-none" style={{ fontSize: '3.8rem' }}>
        99<span className="text-orange-500">.</span>9
      </div>
      <div className="text-xs text-gray-500 tracking-[0.3em] uppercase mt-1 font-mono">% Uptime</div>
    </div>
    <div className="flex-1 border-l border-gray-800 pl-6">
      <div className="text-base font-black text-white uppercase tracking-wide">Cloud</div>
      <div className="text-base font-black text-white uppercase tracking-wide">&amp; DevOps</div>
      <div className="mt-2 flex flex-wrap gap-1">
        {['AWS', 'DOCKER', 'CI/CD', 'ZERO DOWNTIME'].map(t => (
          <span key={t} className="text-[9px] font-mono text-gray-600 border border-gray-800 px-1.5 py-0.5">{t}</span>
        ))}
      </div>
    </div>
  </div>
);

const MobileCard = ({ isActive: _isActive }) => (
  <div className="flex flex-col justify-between h-full">
    <span className="text-xs font-mono text-gray-600 tracking-widest uppercase">04</span>
    <div>
      <div className="text-3xl italic font-medium text-gray-300 leading-none">Mobile</div>
      <div className="text-3xl font-black text-white leading-none uppercase tracking-tight">FIRST</div>
    </div>
    <div className="flex items-center gap-2">
      <span className="text-orange-500 font-black text-lg">+70%</span>
      <span className="text-[10px] text-gray-600 tracking-widest uppercase">on mobile</span>
    </div>
  </div>
);

const SecurityCard = ({ isActive: _isActive }) => (
  <div className="flex flex-col justify-between h-full">
    <span className="text-xs font-mono text-gray-600 tracking-widest uppercase">05</span>
    <div>
      <div className="text-[9px] font-black tracking-[0.55em] text-orange-500 uppercase mb-2">F O R T R E S S</div>
      <div className="text-2xl font-black text-white uppercase leading-tight">Security</div>
      <div className="mt-3 h-1 bg-gray-800 w-full">
        <motion.div
          className="h-full bg-orange-500"
          initial={{ width: 0 }}
          animate={{ width: '95%' }}
          transition={{ duration: 1.4, delay: 0.8, ease: 'easeOut' }}
        />
      </div>
      <div className="text-[10px] text-gray-600 mt-1 tracking-widest">95% PROTECTED</div>
    </div>
    <div className="text-[10px] font-mono text-gray-700 tracking-wider uppercase">DPA COMPLIANT</div>
  </div>
);

const AutomationCard = ({ isActive: _isActive }) => (
  <div className="flex items-center justify-between h-full gap-6">
    <div className="shrink-0">
      <div className="text-[3.5rem] font-black text-orange-500 leading-none">+85</div>
      <div className="text-[10px] text-gray-600 tracking-[0.3em] uppercase font-mono">% automated</div>
    </div>
    <div className="flex-1 border-l border-gray-800 pl-6">
      <div className="text-base font-black uppercase text-white tracking-wide leading-tight">Automation</div>
      <div className="text-base font-black uppercase text-white tracking-wide leading-tight">&amp; Integrations</div>
      <div className="mt-2 flex flex-wrap gap-1">
        {['M-PESA', 'MAILCHIMP', 'CRM', 'ANALYTICS'].map(t => (
          <span key={t} className="text-[9px] font-mono text-gray-600 border border-gray-800 px-1.5 py-0.5">{t}</span>
        ))}
      </div>
    </div>
  </div>
);

const NetworkCard = ({ isActive: _isActive }) => (
  <div className="flex flex-col justify-between h-full">
    <div className="flex items-center justify-between">
      <span className="text-xs font-mono text-gray-600 tracking-widest uppercase">07</span>
      <span className="text-[9px] font-black bg-orange-500 text-white px-1.5 py-0.5 tracking-widest">NEW</span>
    </div>
    <div>
      <div className="text-2xl font-black text-orange-400 uppercase leading-tight tracking-tight">NET</div>
      <div className="text-2xl font-black text-white uppercase leading-tight tracking-tight">WORKING</div>
    </div>
    <div className="font-mono text-[10px] text-orange-500/70 tracking-wider uppercase">99.5% Uptime</div>
  </div>
);

const PolicyCard = ({ isActive: _isActive }) => (
  <div className="flex flex-col justify-between h-full">
    <div className="flex items-center justify-between">
      <span className="text-xs font-mono text-gray-600 tracking-widest uppercase">08</span>
      <span className="text-[9px] font-black bg-orange-500 text-white px-1.5 py-0.5 tracking-widest">NEW</span>
    </div>
    <div>
      <div className="text-[10px] tracking-[0.4em] text-gray-500 uppercase mb-1">Digital</div>
      <div className="text-2xl font-black text-white uppercase leading-tight italic">Policy</div>
      <div className="text-2xl font-black text-orange-500 uppercase leading-tight">100%</div>
    </div>
    <div className="text-[10px] text-gray-700 tracking-wider uppercase font-mono">Compliant</div>
  </div>
);

const MarketingCard = ({ isActive: _isActive }) => (
  <div className="flex flex-col justify-between h-full">
    <div className="flex items-center justify-between">
      <span className="text-xs font-mono text-gray-600 tracking-widest uppercase">09</span>
      <span className="text-[9px] font-black bg-orange-500 text-white px-1.5 py-0.5 tracking-widest">NEW</span>
    </div>
    <div>
      <div className="text-3xl font-black text-white uppercase leading-none tracking-tight">+60%</div>
      <div className="text-xs font-medium text-gray-400 tracking-widest uppercase mt-1">Digital</div>
      <div className="text-lg font-black text-orange-400 uppercase tracking-tight">Marketing</div>
    </div>
    <div className="font-mono text-[10px] text-gray-700 uppercase tracking-wider">SEO · Conversions</div>
  </div>
);

const AVCard = ({ isActive: _isActive }) => (
  <div className="flex flex-col justify-between h-full">
    <div className="flex items-center justify-between">
      <span className="text-xs font-mono text-gray-600 tracking-widest uppercase">10</span>
      <span className="text-[9px] font-black bg-orange-500 text-white px-1.5 py-0.5 tracking-widest">NEW</span>
    </div>
    <div>
      <div className="flex flex-col gap-1 mb-2 opacity-30">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-px bg-orange-400 w-full" />
        ))}
      </div>
      <div className="text-2xl font-black text-white uppercase leading-tight tracking-tight">AV</div>
      <div className="text-sm italic font-medium text-gray-300 tracking-wide">Consulting</div>
    </div>
    <div className="text-[10px] text-orange-500/70 font-mono tracking-widest uppercase">Studio Quality</div>
  </div>
);

const ITSupportCard = ({ isActive: _isActive }) => (
  <div className="flex items-center justify-between h-full gap-6">
    <div className="shrink-0">
      <div className="text-[3rem] font-black text-white leading-none">IT</div>
      <div className="text-xs font-black text-orange-500 uppercase tracking-widest mt-1">Support</div>
    </div>
    <div className="flex-1 border-l border-gray-800 pl-6">
      <div className="text-base font-black uppercase text-white tracking-wide leading-tight">Maintenance</div>
      <div className="text-base font-black uppercase text-white tracking-wide leading-tight">&amp; Management</div>
      <div className="mt-2 flex flex-wrap gap-1">
        {['HARDWARE', 'SOFTWARE', 'PRINTERS', 'BACKUPS'].map(t => (
          <span key={t} className="text-[9px] font-mono text-gray-600 border border-gray-800 px-1.5 py-0.5">{t}</span>
        ))}
      </div>
    </div>
    <div className="shrink-0 text-right">
      <div className="text-[9px] font-black bg-orange-500 text-white px-1.5 py-0.5 tracking-widest mb-1">NEW</div>
      <div className="text-xs font-black text-orange-400 tracking-wider">100%</div>
      <div className="text-[9px] text-gray-600 uppercase tracking-widest">Uptime</div>
    </div>
  </div>
);

const CARD_RENDERS = {
  web: WebDevCard, arch: ArchCard, cloud: CloudCard,
  mobile: MobileCard, security: SecurityCard, automation: AutomationCard,
  network: NetworkCard, policy: PolicyCard, marketing: MarketingCard, av: AVCard,
  it: ITSupportCard,
};

const HeroServiceGrid = ({ onServiceClick }) => {
  const [activeCard, setActiveCard] = useState(null);

  useEffect(() => {
    const ids = CARDS.map(c => c.id);
    let current = 0;
    const pulse = () => {
      const next = Math.random() > 0.4
        ? ids[current % ids.length]
        : ids[Math.floor(Math.random() * ids.length)];
      current++;
      setActiveCard(next);
      setTimeout(() => setActiveCard(null), 900);
    };
    const interval = setInterval(pulse, 1700);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-full h-full" style={{ minHeight: '480px' }}>
      <ScanLine />
      <div className="grid gap-2 h-full" style={{ gridTemplateColumns: '1fr 1fr', gridAutoRows: '1fr' }}>
        {CARDS.map((card, index) => {
          const CardRender = CARD_RENDERS[card.id];
          const isActive = activeCard === card.id;
          return (
            <motion.div
              key={card.id}
              className={`relative overflow-hidden border p-4 cursor-pointer transition-colors duration-300 ${
                card.wide ? 'col-span-2' : ''
              } ${
                isActive
                  ? 'border-orange-500 bg-orange-950/20'
                  : 'border-gray-800 bg-gray-900/60 hover:border-orange-500/50 hover:bg-gray-900/80'
              }`}
              style={{ borderRadius: '2px', minHeight: card.wide ? '80px' : '110px' }}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.06 + 0.3, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -2 }}
              onClick={() => onServiceClick && onServiceClick(CARD_TITLES[card.id])}
            >
              {isActive && (
                <motion.div
                  className="absolute inset-0 pointer-events-none"
                  style={{ background: 'radial-gradient(ellipse at center, rgba(249,115,22,0.15) 0%, transparent 70%)' }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 1, 0.7] }}
                  transition={{ duration: 0.3 }}
                />
              )}
              {isActive && (
                <motion.div
                  className="absolute top-0 left-0 h-px bg-orange-500"
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                />
              )}
              <CardRender isActive={isActive} />
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default HeroServiceGrid;
